import React, { useEffect, useState } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { Search, Bell, User, Activity, Clock, Bot, Sparkles, Settings, LogOut, ChevronDown } from "lucide-react";

import Sidebar from "./components/layout/Sidebar";
import Footer from "./components/Footer";
import { backendURL } from "./config";
import CopilotPanel from "./components/Copilot/CopilotPanel";

// Pages
import PulseCommandCenter from "./pages/PulseCommandCenter";
import ModernDashboard from "./pages/ModernDashboard";
import Analysis from "./pages/Analysis";
import AIThreatInsights from "./pages/AIThreatInsights";
import DarkwebIntelligence from "./pages/DarkwebIntelligence";
import DarkWebMonitoring from "./pages/DarkWebMonitoring";
import InfoLogs from "./pages/InfoLogs";
import ErrorLogs from "./pages/ErrorLogs";
import WarningLogs from "./pages/WarningLogs";
import GlobalThreatMap from "./pages/GlobalThreatMap";
import AssetIdentity from "./pages/AssetIdentity";
import EventExplorer from "./pages/EventExplorer";
import Incidents from "./pages/Incidents";
import MitreCoverage from "./pages/MitreCoverage";
import EventMonitor from "./pages/EventMonitor";
import SOCDashboard from "./pages/SOCDashboard";
import Tickets from "./pages/Tickets";
import GlobalSearch from "./pages/GlobalSearch";
import NetworkTrafficAnalysis from "./pages/NetworkTrafficAnalysis";
import EarlyWarning from "./pages/EarlyWarning";
import ThreatHunt from "./pages/ThreatHunt";
import ThreatIntel from "./pages/ThreatIntel";
import ThreatIntelReportDetail from "./pages/ThreatIntelReportDetail";
import Investigations from "./pages/Investigations";
import InvestigationDetail from "./pages/InvestigationDetail";
import InvestigationAsk from "./pages/InvestigationAsk";
import DetectionCoverage from "./pages/DetectionCoverage";
import DetectionIntelligence from "./pages/DetectionIntelligence";
import DetectionRules from "./pages/DetectionRules";
import RuleRecommendations from "./pages/RuleRecommendations";
import DetectionRouting from "./pages/DetectionRouting";
import CasesBoard from "./pages/CasesBoard";
import ResponseActions from "./pages/ResponseActions";
import WorkflowsBuilder from "./pages/WorkflowsBuilder";
import WorkflowDesigner from "./pages/WorkflowDesigner";
import Runbooks from "./pages/Runbooks";
import SkillLibrary from "./pages/SkillLibrary";
import SkillEditor from "./pages/SkillEditor";
import EnterpriseInsights from "./pages/EnterpriseInsights";
import PostureScore from "./pages/PostureScore";
import PostureImprove from "./pages/PostureImprove";
import CaseRoom from "./pages/CaseRoom";
import FirewallApprovals from "./pages/FirewallApprovals";
import ReportsCenter from "./pages/ReportsCenter";
import ThreatDownEDR from "./pages/ThreatDownEDR";
import MicrosoftDefender from "./pages/MicrosoftDefender";
import EmailSecurityMXDR from "./pages/EmailSecurityMXDR";
import EntraIdentityProtection from "./pages/EntraIdentityProtection";
import DefenderTVM from "./pages/DefenderTVM";
import PurviewAuditExplorer from "./pages/PurviewAuditExplorer";
import AttackStoryExplorer from "./pages/AttackStoryExplorer";
import AttackSurfaceMonitor from "./pages/AttackSurfaceMonitor";
import ExecutiveBrief from "./pages/ExecutiveBrief";
import MachineConnector from "./pages/MachineConnector";
import InfraMonitoring from "./pages/InfraMonitoring";

function getStoredUser() {
    try {
        const raw = localStorage.getItem("user");
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && (parsed.name || parsed.email)) return parsed;
        }
    } catch (e) {}

    const token = localStorage.getItem("token");
    if (token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const decoded = JSON.parse(jsonPayload);
            if (decoded) {
                return {
                    name: decoded.name || localStorage.getItem("userName") || "",
                    email: decoded.email || localStorage.getItem("userEmail") || "",
                    role: decoded.role || localStorage.getItem("userRole") || "admin"
                };
            }
        } catch (e) {}
    }

    return {
        name: localStorage.getItem("userName") || "",
        email: localStorage.getItem("userEmail") || "",
        role: localStorage.getItem("userRole") || "admin"
    };
}

const Header = ({ onOpenCopilot, currentUser }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [searchQuery, setSearchQuery] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSearch = (e) => {
        if (e.key === "Enter" && searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const displayName = currentUser?.name || (currentUser?.email ? currentUser.email.split("@")[0].split(/[._-]/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ") : (localStorage.getItem("userName") || "Security Analyst"));
    const initials = displayName
        .split(" ")
        .filter(Boolean)
        .map(n => n[0].toUpperCase())
        .slice(0, 2)
        .join("") || "SA";
    const displayEmail = currentUser?.email || localStorage.getItem("userEmail") || "analyst@querytel.com";
    const displayRole = currentUser?.role ? (currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1)) : "Admin";

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    return (
        <header className="sticky top-0 z-40 h-14 px-6 flex items-center justify-between bg-[#0B1120] border-b border-white/[0.06]">
            <div className="flex items-center gap-5">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider">Operational</span>
                </div>

                <div className="hidden lg:flex items-center bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 w-80 group focus-within:border-blue-500/30 transition-colors">
                    <Search className="w-3.5 h-3.5 text-slate-600 group-focus-within:text-blue-400 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search alerts, IPs, assets..."
                        className="bg-transparent border-none outline-none text-[12px] text-slate-300 ml-2.5 w-full placeholder:text-slate-600 ring-0 focus:ring-0"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearch}
                    />
                    <span className="text-[9px] font-medium text-slate-700 bg-white/[0.04] px-1.5 py-0.5 rounded ml-2">⌘K</span>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={onOpenCopilot}
                    className="hidden xl:flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 border border-blue-500/20 rounded-lg text-blue-400 text-[11px] font-medium hover:bg-blue-600/20 transition-colors"
                >
                    <Bot className="w-3.5 h-3.5" />
                    <span>AI Copilot</span>
                </button>

                <div className="hidden xl:flex items-center gap-2 text-slate-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-mono">{currentTime.toLocaleTimeString()}</span>
                </div>

                <button className="relative p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] transition-colors">
                    <Bell className="w-4 h-4" />
                    <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-rose-500 rounded-full" />
                </button>

                <div className="w-px h-6 bg-white/[0.06] mx-1" />

                {/* User Profile Badge with Dynamic Initials and Name */}
                <div className="relative">
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="flex items-center gap-2.5 px-2 py-1 rounded-lg hover:bg-white/[0.05] transition-colors group cursor-pointer focus:outline-none border border-transparent hover:border-white/[0.06]"
                    >
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 via-indigo-600 to-blue-700 flex items-center justify-center text-[10px] font-bold text-white shadow-sm ring-1 ring-white/10">
                            {initials}
                        </div>
                        <div className="hidden sm:flex flex-col text-left">
                            <span className="text-[12px] font-semibold text-slate-200 group-hover:text-white transition-colors leading-tight">
                                {displayName}
                            </span>
                            <span className="text-[9.5px] text-slate-500 font-mono leading-tight">
                                {displayRole}
                            </span>
                        </div>
                        <ChevronDown className={`w-3 h-3 text-slate-500 group-hover:text-slate-300 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {menuOpen && (
                        <div
                            className="absolute right-0 mt-2 w-56 bg-[#111827] border border-white/[0.08] rounded-xl shadow-2xl shadow-black/70 p-2 z-50 backdrop-blur-xl"
                            onMouseLeave={() => setMenuOpen(false)}
                        >
                            <div className="px-3 py-2 border-b border-white/[0.06] mb-1">
                                <p className="text-xs font-semibold text-white truncate">{displayName}</p>
                                <p className="text-[10px] text-slate-400 font-mono truncate">{displayEmail}</p>
                                <div className="mt-1.5 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                    <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-semibold">{displayRole}</span>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors cursor-pointer"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default function ProtectedApp() {
    const [collapsed, setCollapsed] = useState(false);
    const [hoverExpand, setHoverExpand] = useState(false);
    const [isCopilotOpen, setIsCopilotOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(getStoredUser);
    const token = localStorage.getItem("token");

    useEffect(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        if (saved !== null) setCollapsed(saved === "true");
    }, []);

    useEffect(() => {
        if (!token) return;
        fetch(`${backendURL}/api/me`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                if (data?.user) {
                    setCurrentUser(data.user);
                    localStorage.setItem("user", JSON.stringify(data.user));
                    if (data.user.name) localStorage.setItem("userName", data.user.name);
                    if (data.user.email) localStorage.setItem("userEmail", data.user.email);
                }
            })
            .catch(() => {});
    }, [token]);

    const toggleCollapse = () => {
        const newValue = !collapsed;
        setCollapsed(newValue);
        localStorage.setItem("sidebarCollapsed", newValue);
    };

    const effectiveCollapsed = collapsed && !hoverExpand;
    const location = useLocation();
    const isMapPage = location.pathname === "/global-map";

    if (!token) {
        return (
            <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0B1120] flex">
            <div className={`transition-all duration-300 ${effectiveCollapsed ? 'w-[68px]' : 'w-[240px]'}`}>
                <Sidebar
                    collapsed={effectiveCollapsed}
                    onToggle={toggleCollapse}
                    onHoverStart={() => setHoverExpand(true)}
                    onHoverEnd={() => setHoverExpand(false)}
                    onOpenCopilot={() => setIsCopilotOpen(true)}
                    currentUser={currentUser}
                />
            </div>

            <div className="flex-1 flex flex-col min-w-0">
                <Header onOpenCopilot={() => setIsCopilotOpen(true)} currentUser={currentUser} />
                <main className={`flex-1 overflow-y-auto ${isMapPage ? 'p-0' : 'p-5'}`}>
                    <Routes>
                        <Route path="/" element={<PulseCommandCenter />} />
                        <Route path="/dashboard" element={<ModernDashboard />} />
                        <Route path="/analysis" element={<Analysis />} />
                        <Route path="/ai-threat-insights" element={<AIThreatInsights />} />
                        <Route path="/darkweb-intelligence" element={<DarkwebIntelligence />} />
                        <Route path="/darkweb-monitor" element={<DarkWebMonitoring />} />
                        <Route path="/logs" element={<EventExplorer />} />
                        <Route path="/logs/explorer" element={<EventExplorer />} />
                        <Route path="/logs/live" element={<InfoLogs />} />
                        <Route path="/logs/info" element={<InfoLogs />} />
                        <Route path="/logs/warning" element={<WarningLogs />} />
                        <Route path="/logs/search" element={<GlobalSearch />} />
                        <Route path="/global-map" element={<GlobalThreatMap />} />
                        <Route path="/asset-identity" element={<AssetIdentity />} />
                        <Route path="/event-explorer" element={<EventExplorer />} />
                        <Route path="/incidents" element={<Incidents />} />
                        <Route path="/mitre-coverage" element={<MitreCoverage />} />
                        <Route path="/event-monitor" element={<EventMonitor />} />
                        <Route path="/soc-dashboard" element={<SOCDashboard />} />
                        <Route path="/tickets" element={<Tickets />} />
                        <Route path="/search" element={<GlobalSearch />} />
                        <Route path="/network-traffic" element={<NetworkTrafficAnalysis />} />
                        <Route path="/early-warning" element={<EarlyWarning />} />
                        <Route path="/threat-hunt" element={<ThreatHunt />} />
                        <Route path="/threat-intel" element={<ThreatIntel />} />
                        <Route path="/threat-intel/:reportId" element={<ThreatIntelReportDetail />} />
                        <Route path="/hunt/threat-hunts/intel/:reportId" element={<ThreatIntelReportDetail />} />
                        <Route path="/investigations" element={<Investigations />} />
                        <Route path="/investigations/:caseId" element={<InvestigationDetail />} />
                        <Route path="/investigations/:caseId/ask" element={<InvestigationAsk />} />
                        <Route path="/investigation/summary" element={<InvestigationDetail />} />
                        <Route path="/investigation/ask" element={<InvestigationAsk />} />
                        <Route path="/detection-coverage" element={<DetectionCoverage />} />
                        <Route path="/detect/detection-coverage" element={<DetectionCoverage />} />
                        <Route path="/detection-intelligence" element={<DetectionIntelligence />} />
                        <Route path="/detect/detection-intelligence" element={<DetectionIntelligence />} />
                        <Route path="/detection-rules" element={<DetectionRules />} />
                        <Route path="/detect/detection-rules" element={<DetectionRules />} />
                        <Route path="/detection-rules/:ruleId/recommendations" element={<RuleRecommendations />} />
                        <Route path="/detect/detection-rules/:ruleId/recommendations" element={<RuleRecommendations />} />
                        <Route path="/detection-routing" element={<DetectionRouting />} />
                        <Route path="/detect/detection-routing" element={<DetectionRouting />} />
                        <Route path="/cases" element={<CasesBoard />} />
                        <Route path="/respond/cases" element={<CasesBoard />} />
                        <Route path="/respond/case" element={<CaseRoom />} />
                        <Route path="/respond/cases/:caseId" element={<CaseRoom />} />
                        <Route path="/respond/case/:caseId" element={<CaseRoom />} />
                        <Route path="/cases/:caseId" element={<CaseRoom />} />
                        <Route path="/response-actions" element={<ResponseActions />} />
                        <Route path="/respond/actions" element={<ResponseActions />} />
                        <Route path="/respond/cases/tasks" element={<ResponseActions />} />
                        <Route path="/approvals" element={<FirewallApprovals />} />
                        <Route path="/firewall-approvals" element={<FirewallApprovals />} />
                        <Route path="/respond/approvals" element={<FirewallApprovals />} />
                        <Route path="/workflows" element={<WorkflowsBuilder />} />
                        <Route path="/respond/workflows" element={<WorkflowsBuilder />} />
                        <Route path="/workflows/designer" element={<WorkflowDesigner />} />
                        <Route path="/respond/workflows/designer" element={<WorkflowDesigner />} />
                        <Route path="/runbooks" element={<Runbooks />} />
                        <Route path="/respond/runbooks" element={<Runbooks />} />
                        <Route path="/skills" element={<SkillLibrary />} />
                        <Route path="/hunt/skills" element={<SkillLibrary />} />
                        <Route path="/skills/:skillId" element={<SkillEditor />} />
                        <Route path="/hunt/skills/:skillId" element={<SkillEditor />} />
                        <Route path="/insights" element={<EnterpriseInsights />} />
                        <Route path="/insight" element={<EnterpriseInsights />} />
                        <Route path="/enterprise-insights" element={<EnterpriseInsights />} />
                        <Route path="/posture" element={<PostureScore />} />
                        <Route path="/posture/score" element={<PostureScore />} />
                        <Route path="/posture/improve" element={<PostureImprove />} />
                        <Route path="/improve-posture" element={<PostureImprove />} />
                        <Route path="/posture-management" element={<PostureScore />} />
                        <Route path="/reports" element={<ReportsCenter />} />
                        <Route path="/reports/generator" element={<ReportsCenter />} />
                        <Route path="/reports/:templateId" element={<ReportsCenter />} />
                        <Route path="/reports-center" element={<ReportsCenter />} />
                        <Route path="/threatdown" element={<ThreatDownEDR />} />
                        <Route path="/malwarebytes" element={<ThreatDownEDR />} />
                        <Route path="/edr" element={<ThreatDownEDR />} />
                        <Route path="/defender" element={<MicrosoftDefender />} />
                        <Route path="/microsoft-defender" element={<MicrosoftDefender />} />
                        <Route path="/email-security" element={<EmailSecurityMXDR />} />
                        <Route path="/mxdr" element={<EmailSecurityMXDR />} />
                        <Route path="/threatgate" element={<EmailSecurityMXDR />} />
                        <Route path="/identity-protection" element={<EntraIdentityProtection />} />
                        <Route path="/entra-id" element={<EntraIdentityProtection />} />
                        <Route path="/tvm" element={<DefenderTVM />} />
                        <Route path="/vulnerabilities" element={<DefenderTVM />} />
                        <Route path="/purview" element={<PurviewAuditExplorer />} />
                        <Route path="/audit-search" element={<PurviewAuditExplorer />} />
                        <Route path="/compliance" element={<PurviewAuditExplorer />} />
                        <Route path="/attack-story" element={<AttackStoryExplorer />} />
                        <Route path="/kill-chain" element={<AttackStoryExplorer />} />
                        <Route path="/attack-surface" element={<AttackSurfaceMonitor />} />
                        <Route path="/easm" element={<AttackSurfaceMonitor />} />
                        <Route path="/executive-brief" element={<ExecutiveBrief />} />
                        <Route path="/board-report" element={<ExecutiveBrief />} />
                        <Route path="/machines" element={<MachineConnector />} />
                        <Route path="/endpoints" element={<MachineConnector />} />
                        <Route path="/monitoring" element={<InfraMonitoring />} />
                        <Route path="/infrastructure" element={<InfraMonitoring />} />
                    </Routes>
                </main>
                <Footer />
            </div>

            <CopilotPanel isOpen={isCopilotOpen} onClose={() => setIsCopilotOpen(false)} />
        </div>
    );
}
